import _ from 'lodash'
import type {
	Component,
	Spx,
	Style,
	InnerElementStyle,
	ResolvedInnerElementsDataItem,
	InnerElementsMap,
	ResolvedBuilderWidgetDataItem,
	ReducedManifest,
	PathScopedCustomEnums,
} from '@wix/thunderbolt-becky-types'
import { getStyleCssVars } from '@wix/thunderbolt-becky-root'
import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import { mapValuesValues } from '../../utils/mapValuesValues'
import { mapValues } from '../../utils/mapValues'
import type { VariantRelationViewItem, RefArrayViewItem, InflatedRefArrayViewItem, InflatedRefArrayViewItemValues } from '@wix/thunderbolt-catharsis'
import { envRefs, themeRefs } from '@wix/thunderbolt-catharsis'
import { clearFutileOverridesFromCssObject } from '../../utils/futileCssOptimizer'
import { toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { isRefArray, isVariantRelation } from '../../utils/variantsUtils'
import { createEmptyStyleItem } from './compStylesCompNode'
import { stylePropsToCssVars, customEnumsToCssVar, processBuilderStyle } from './styleProps/stylePropsToCss'
import { flattenNestedStructures } from '../../utils/flattenNestedStructures'
import type { BuilderStyle } from '@wix/document-services-types'

type InnerElementRawItems = InflatedRefArrayViewItem<Style>['values']
type InnerElementProcessedItems = RefArrayViewItem<Style>['values']

const COMP_TYPES_WITH_SPECIFIC_VARS = new Set([
	'TPASection',
	'TPAMultiSection',
	'TPAWidget',
	'TPAGluedWidget',
	'tpaWidgetNative',
	'SlideShowSlide',
])

const styleToCssVars = (
	style: Style,
	siteColors: Array<string>,
	isScalePropExperimentOn: boolean,
	themeSpx: Spx | undefined,
	componentsSkinParams: any
) => {
	if (!style?.style?.properties || 'st-css' in style.style.properties) {
		return undefined
	}
	return getStyleCssVars(style, isScalePropExperimentOn, themeSpx, siteColors, componentsSkinParams)
}

const getStyleFromReducedManifest = (reducedManifest?: ReducedManifest): PathScopedCustomEnums | undefined => {
	if (!reducedManifest) {
		return undefined
	}

	const pathScopedMap: PathScopedCustomEnums = {}

	for (const elementPath in reducedManifest.elementData) {
		const elementData = reducedManifest.elementData[elementPath]
		if (elementData && elementData.customEnum) {
			pathScopedMap[elementPath] = elementData.customEnum
		}
	}

	return Object.keys(pathScopedMap).length > 0 ? pathScopedMap : undefined
}

const compThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) =>
		COMP_TYPES_WITH_SPECIFIC_VARS.has(component.componentType)
			? null
			: {
				compStyles: refs.compStyles(component),
				sitesEditorName: envRefs.editorName,
				theme: themeRefs.theme,
				experiments: envRefs.experiments,
				siteColors: themeRefs.siteColors,
				componentsSkinParams: envRefs.componentsSkinParams,
				themeFonts: themeRefs.themeFonts,
				reducedManifest: component.builderType ? envRefs.builderTypeToReducedManifest(component.builderType) : undefined,
			},
	toViewItem: (component, deps) => {
		if (!deps || !deps.compStyles) {
			return null
		}

		const { compStyles, sitesEditorName, theme, experiments, siteColors, reducedManifest } = deps

		const pathScopedCustomEnums = reducedManifest ? getStyleFromReducedManifest(reducedManifest) : undefined

		const isSiteFromEditor = sitesEditorName === 'Studio' || !!experiments['specs.thunderbolt.forceStudio']
		if (compStyles.type === 'classic') {
			return {
				type: 'classic' as const,
				css: mapValues(compStyles.css, (css: Style) =>
					styleToCssVars(css, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
				),
			}
		}

		const variantsCss = mapValuesValues(compStyles.css, (css: Style | BuilderStyle) => {
			if (component.builderType && css) {
				return experiments['specs.thunderbolt.newStyleApi']
					? processBuilderStyle(css as Style, pathScopedCustomEnums, 'root')
					: stylePropsToCssVars(css as BuilderStyle, pathScopedCustomEnums, 'root')
			}

			return styleToCssVars(css as Style, siteColors, isSiteFromEditor, theme?.spx, deps.componentsSkinParams)
		})
		return {
			type: 'variants' as const,
			css: variantsCss,
		}
	},
})

export const innerElementTheme = createCssCompNode('styleBased', 'innerElementTheme', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => ({
		innerElementsStyles: component.innerElementsQuery ? refs.innerElementsStyles(component) : undefined,
		innerElementsSelectors: envRefs.selector(component.id),
		theme: themeRefs.theme,
		themeFonts: themeRefs.themeFonts,
		sitesEditorName: envRefs.editorName,
		experiments: envRefs.experiments,
		reducedManifest: component.builderType ? envRefs.builderTypeToReducedManifest(component.builderType) : undefined,
	}),
	toViewItem: (component, deps) => {
		if (!deps || _.isEmpty(deps.innerElementsStyles)) {
			return null
		}

		const { innerElementsStyles, innerElementsSelectors, reducedManifest, experiments } = deps

		const pathScopedCustomEnums = reducedManifest ? getStyleFromReducedManifest(reducedManifest) : undefined
		const cssVars = _.reduce(
			innerElementsStyles,
			(result, value, key) => {
				const styles = _.reduce(
					value,
					(res, val, breakpoint) => {
						const styleData = _.get(val, [''])
						res[breakpoint] = experiments['specs.thunderbolt.newStyleApi']
							? processBuilderStyle(styleData as Style, pathScopedCustomEnums, key)
							: stylePropsToCssVars(styleData, pathScopedCustomEnums, key)

						return res
					},
					{} as { [breakpoint: string]: {} }
				)

				result[key] = styles
				return result
			},
			{} as { [innerElementId: string]: {} }
		)

		return {
			type: 'variants' as const,
			css: cssVars,
			innerElementsSelectors,
		}
	},
})

export const innerElementsStyles = createCssCompNode('styleBased', 'innerElementsStyles', {
	getDependencies: (component: Component, refs: FeatureRefs<'styleBased'>) => {
		return {
			widgetDataItem: component.innerElementsQuery
				? refs.innerElementsQuery(component.innerElementsQuery)
				: undefined,
			experiments: envRefs.experiments,
		}
	},
	toViewItem: (component, deps) => {
		if (!deps?.widgetDataItem) {
			return null
		}
		const { widgetDataItem, experiments } = deps

		const generateInnerElementsToStyleMap = (
			widgetDataItem: ResolvedBuilderWidgetDataItem,
			parentKey: string = ''
		): InnerElementsMap => {
			const result: InnerElementsMap = {}

			const innerElements = widgetDataItem?.innerElements
			if (!innerElements) {
				return result
			}

			for (const innerElementName in innerElements) {
				if (innerElements[innerElementName] && typeof innerElements[innerElementName] === 'object') {
					const combinedKey = parentKey ? `${parentKey}/${innerElementName}` : innerElementName

					if ('styleId' in innerElements[innerElementName]) {
						result[combinedKey] = innerElements[innerElementName]
					}

					if ('innerElementsQuery' in innerElements[innerElementName]) {
						const element = innerElements[innerElementName] as ResolvedInnerElementsDataItem
						const nestedResult = generateInnerElementsToStyleMap(element.innerElementsQuery, combinedKey)
						Object.assign(result, nestedResult)
					}
				}
			}

			return result
		}

		const innerElements = generateInnerElementsToStyleMap(widgetDataItem)

		return (_.mapValues(innerElements, (innerElement) => {
			const rawItems = isRefArray(innerElement.styleId)
				? innerElement.styleId.values
				: [innerElement.styleId || createEmptyStyleItem(component)]

			const items: InnerElementProcessedItems = experiments['specs.thunderbolt.newStyleApi']
				? flattenNestedStructures<Style>(rawItems as InnerElementRawItems)
				: rawItems as InnerElementProcessedItems

			return toBreakpointVariantsMap<Style, Style>(items, (item) => item)
		}) as unknown) as {
			[innerElementId: string]: InnerElementStyle
		}
	},
})

const viewerCompThemeCompNode = createCssCompNode('styleBased', 'compTheme', {
	getDependencies: compThemeCompNode.getDependencies,
	toViewItem: (component, deps) => {
		const compTheme = compThemeCompNode.toViewItem(component, deps)
		if (!compTheme || compTheme.type === 'classic') {
			return compTheme
		}
		return {
			...compTheme,
			css: clearFutileOverridesFromCssObject(compTheme.css),
		}
	},
})

export const compTheme = (isEditor: boolean) => (isEditor ? compThemeCompNode : viewerCompThemeCompNode)
